import axios from 'axios';
import attachAuthToken from '../axiosConfig';
// SyncJobStatus nao entra aqui: depois que fetchJobs passou a receber tipo/status como
// string (o HistoricoSection e compartilhado), ele so e reexportado, nunca usado local.
import type { JobResumo, JobCompleto } from '../../components/sync-painel/types';
import { clienteDoLogGeral } from '../logGeral/clienteDoLogGeral';

/**
 * Painel de Sincronização da Base (banco-de-dados-atual) — o serviço ETL que alimenta o
 * Contac (Cloud Run, backend próprio). Mesmo desenho dos outros módulos satélites
 * (Portal de Cotações, Cilia): o módulo só existe na UI quando a env aponta para o
 * backend dele, e as chamadas levam o JWT do SOAC (o backend do banco valida o token e
 * exige ADMIN/TI quando a env SOAC_JWT_SECRET dele está definida).
 *
 * ⚠️ Sem default para `localhost`: um fallback faria o módulo aparecer em produção
 * apontando para a máquina de quem abriu a tela, e o gate viraria decoração.
 */
export const BANCO_SYNC_HABILITADO = Boolean(process.env.REACT_APP_BANCO_DADOS_URL);

const BASE_URL = process.env.REACT_APP_BANCO_DADOS_URL ?? '';

const api = axios.create({ baseURL: BASE_URL });
attachAuthToken(api);

/** Log geral deste serviço (transversal/at-91), lido pela tela Logs do sistema do SOAC. */
export const logGeralDoBanco = clienteDoLogGeral(api);

// ---------------------------------------------------------------------------
// Tipos (contrato do backend do banco — src/sync do banco-de-dados-atual)
// ---------------------------------------------------------------------------

/**
 * Os tipos de job vivem em components/sync-painel/types.ts desde que o painel da FIPE
 * passou a usar os mesmos componentes. Reexportados aqui para os imports existentes
 * continuarem valendo.
 */
export type { SyncJobStatus, EtapaResult, JobResumo, JobCompleto } from '../../components/sync-painel/types';
export type BlocoTipo = 'bloco1' | 'bloco2' | 'bloco3';
/**
 * Tipos de job no histórico: os 3 blocos, o reprocessamento rastreado (22/08/2026) e o
 * sync de eventos (28/08/2026). ⚠️ `eventos` NÃO é um bloco — não faz parte do runbook de
 * atualização completa, tem cron próprio (05h) e vive fora de `BlocoTipo` de propósito.
 * Ele aparece na mesma resposta do overview só para o painel não gastar outra requisição.
 */
export type JobTipo = BlocoTipo | 'eventos' | 'recover';
export type RecoverOrigem = 'associados' | 'veiculos' | 'entradas-financeiras' | 'eventos' | 'nasajon';

export interface FalhaRelatorioItem {
  id: string;
  origem: 'associados' | 'veiculos' | 'entradas-financeiras' | 'nasajon' | string;
  arquivo: string;
  criadoEm: string;
  processadoEm: string | null;
  resumo: string;
}

export interface Overview {
  agora: string;
  blocos: {
    bloco1: JobResumo | null;
    bloco2: JobResumo | null;
    bloco3: JobResumo | null;
    /**
     * Sync de eventos (sinistros). Fica dentro de `blocos` porque é assim que o backend
     * responde — mas não é um bloco do runbook: é sync próprio, com cron diário.
     * `null` = nunca executado.
     */
    eventos: JobResumo | null;
    /**
     * Varredura de boletos por data de VENCIMENTO (comissionamentos/at-01). Também não é bloco
     * do runbook — é sync próprio, cron das 04h, e alimenta as telas de Comissionamento.
     * ⚠️ Divide o lock do SGA com bloco1/bloco2/eventos, então PRECISA entrar no `sgaOcupado`
     * da tela: sem isso os botões dos outros ficam clicáveis durante a varredura e morrem em 409.
     */
    boletosVencimento: JobResumo | null;
  };
  falhasPendentes: {
    total: number;
    porOrigem: Record<string, number>;
    itens: FalhaRelatorioItem[];
  };
  viewsRefresh: Array<{ id: number; refreshed_at: string; duration_ms: string }> | null;
  crons: Array<{ quando: string; oque: string }>;
}

/**
 * `GET /eventos/resumo` — contagem local do espelho de eventos, por base.
 * ⚠️ Enquanto a migration da at-09 não for aplicada no banco, esta rota responde 500
 * (as tabelas não existem). A tela precisa distinguir isso de "nenhum evento".
 */
export interface ResumoEventos {
  periodoPadraoBackfill: { desde: string; ate: string };
  porBase: Array<{ base: string; total: number; ultimoCadastro: string | null }>;
}

export interface DisparoResposta {
  message: string;
  jobId: string;
  statusUrl: string;
}

/** 409: já existe job em execução — o body traz qual. */
export interface ConflitoResposta {
  message: string;
  jobId: string;
  tipo?: string;
  statusUrl: string;
}

export interface ViewInfo {
  view: string;
  tier?: number;
  descricao?: string;
  [k: string]: unknown;
}

export interface UploadEasyResposta {
  insertedCount: number;
  skippedCount: number;
  [k: string]: unknown;
}

export interface UploadRecorrenteResposta {
  atualizados: number;
  falhas: Array<{ linha?: number; erro?: string; [k: string]: unknown }>;
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// Leitura (painel)
// ---------------------------------------------------------------------------

export const fetchOverview = async (): Promise<Overview> => {
  const { data } = await api.get<Overview>('/sync/overview');
  return data;
};

// tipo/status como string: o HistoricoSection e compartilhado entre os paineis e passa os
// valores das opcoes que a propria tela declara. Quem valida e o backend.
export const fetchJobs = async (params: {
  tipo?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ total: number; page: number; pageSize: number; jobs: JobResumo[] }> => {
  const { data } = await api.get('/sync/jobs', { params });
  return data;
};

export const fetchJobDetalhe = async (id: string): Promise<JobCompleto> => {
  const { data } = await api.get<JobCompleto>(`/sync/jobs/${id}`);
  return data;
};

export const fetchStatusBloco = async (tipo: BlocoTipo): Promise<JobCompleto> => {
  const { data } = await api.get<JobCompleto>(`/sync/${tipo}/status`);
  return data;
};

/**
 * Status da varredura de boletos por vencimento (comissionamentos/at-01).
 *
 * Função própria em vez de entrar em `fetchStatusBloco`: `boletos-vencimento` **não é um bloco**
 * do runbook (mesma razão de `eventos` viver fora de `BlocoTipo`), e alargar aquele tipo faria a
 * varredura aparecer como quarto bloco nos controles do painel.
 *
 * ⚠️ Responde **404 quando nunca foi executada** — que não é erro, é ausência. Quem chama trata
 * como "nenhuma execução ainda"; confundir isso com falha faria a tela dizer que não conseguiu
 * verificar quando a resposta foi clara.
 */
export const fetchStatusBoletosVencimento = async (): Promise<JobCompleto | null> => {
  try {
    const { data } = await api.get<JobCompleto>('/sync/boletos-vencimento/status');
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

export const fetchFalhas = async (params: {
  origem?: string;
  pendentes?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{ total: number; page: number; pageSize: number; itens: FalhaRelatorioItem[] }> => {
  const { data } = await api.get('/sync/falhas', {
    params: { ...params, pendentes: params.pendentes ? 'true' : undefined },
  });
  return data;
};

export const fetchFalhaDetalhe = async (id: string): Promise<FalhaRelatorioItem & { payload: unknown }> => {
  const { data } = await api.get(`/sync/falhas/${id}`);
  return data;
};

export const fetchViews = async (): Promise<ViewInfo[]> => {
  const { data } = await api.get<ViewInfo[]>('/views');
  return data;
};

// ---------------------------------------------------------------------------
// Disparos (jobs em background — 202 com jobId; 409 quando já há job rodando)
// ---------------------------------------------------------------------------

export const dispararBloco1 = async (): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/sync/bloco1');
  return data;
};

export const dispararBloco2 = async (periodo: {
  data_emissao_inicial: string;
  data_emissao_final: string;
}): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/sync/bloco2', periodo);
  return data;
};

/**
 * Dispara a varredura de boletos por data de VENCIMENTO (comissionamentos/at-01).
 *
 * Sem parâmetros: a janela é sempre os 2 meses (mês anterior + corrente), calculada no servidor
 * em horário de Brasília. Alcançada por ADMIN, TI e COMISSIONAMENTO — este último é o único papel
 * operacional com acesso a uma rota deste backend, declarado por rota (@PapeisExtras), não na
 * lista global de papéis do guard.
 */
export const dispararBoletosVencimento = async (): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/sync/boletos-vencimento');
  return data;
};

export const dispararBloco3 = async (opts: { mode: 'full' } | { mode: 'incremental'; desde: string }): Promise<DisparoResposta> => {
  const params = opts.mode === 'incremental' ? { mode: 'incremental', desde: opts.desde } : { mode: 'full' };
  const { data } = await api.post<DisparoResposta>('/sync/bloco3', undefined, { params });
  return data;
};

/**
 * Backfill de eventos: varre `data_cadastro` em janelas MENSAIS (a API da Hinova recusa
 * intervalo acima de 30 dias). Sem datas, o backend varre de 01/01/2025 até hoje.
 * Idempotente — a chave é (codigo, base_tag), então repetir uma janela não duplica.
 *
 * ⚠️ Datas em DD/MM/YYYY e como QUERY PARAM (não body): é o contrato da rota jobificada.
 */
export const dispararEventosBackfill = async (periodo: {
  desde?: string;
  ate?: string;
}): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/sync/eventos', undefined, {
    params: {
      desde: periodo.desde || undefined,
      ate: periodo.ate || undefined,
    },
  });
  return data;
};

/**
 * Incremental de eventos — o mesmo que o cron das 05h roda: janela recente de
 * `data_cadastro` mais o rejanelamento dos meses que ainda têm evento em situação não
 * terminal. É assim que mudança de situação de evento antigo é capturada (ela não altera
 * a data de cadastro). Padrão e teto: 30 dias, limite da API.
 */
export const dispararEventosIncremental = async (dias?: number): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/sync/eventos/incremental', undefined, {
    params: dias ? { dias } : undefined,
  });
  return data;
};

/**
 * Estado do espelho local de eventos, por base. É a resposta a "o backfill funcionou?" —
 * o status do job diz que ele rodou, isto diz o que ficou gravado.
 */
export const fetchResumoEventos = async (): Promise<ResumoEventos> => {
  const { data } = await api.get<ResumoEventos>('/eventos/resumo');
  return data;
};

/**
 * Reprocessa o que falhou (relatório pendente da origem, ou um arquivo específico) —
 * como JOB em background (202 + jobId), rastreado no histórico com etapas. Substituiu
 * as chamadas síncronas aos recovers de cada módulo em 22/08/2026.
 */
export const reprocessarOrigem = async (origem: RecoverOrigem, arquivo?: string): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>(`/sync/recover/${origem}`, undefined, {
    params: arquivo ? { file: arquivo } : undefined,
  });
  return data;
};

/** Renova os tokens Hinova (RJ+BH) — a causa nº 1 de janela pulada é autenticação. */
export const renovarTokenHinova = async (): Promise<unknown> => {
  const { data } = await api.post('/api-key/refresh');
  return data;
};

// ---------------------------------------------------------------------------
// Uploads e views
// ---------------------------------------------------------------------------

export const uploadEasyAssist = async (file: File): Promise<UploadEasyResposta> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<UploadEasyResposta>('/easy-saida-financeira/upload', form);
  return data;
};

export const uploadRecorrentes = async (file: File): Promise<UploadRecorrenteResposta> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<UploadRecorrenteResposta>('/recorrente/upload-csv', form);
  return data;
};

export const refreshTodasViews = async (): Promise<{ views: string[]; duration_ms: number }> => {
  const { data } = await api.post('/views/refresh', { mode: 'refresh' });
  return data;
};

// ---------------------------------------------------------------------------
// Totais por competência (23/08/2026) — dois lados deliberadamente diferentes:
// tabelas = bruto (tudo que existe); views = o que o Contac lê (regras embutidas).
// ---------------------------------------------------------------------------

export interface TotalGrupo {
  rotulo: string;
  tipo: 'ENTRADA' | 'SAIDA' | 'RESULTADO';
  total: number;
  porCompetencia: Record<string, number>;
}

export interface TotaisResposta {
  inicio: string;
  fim: string;
  competencias: string[];
  grupos: TotalGrupo[];
}

export const fetchTotais = async (lado: 'tabelas' | 'views', periodo: { inicio: string; fim: string }): Promise<TotaisResposta> => {
  const { data } = await api.get<TotaisResposta>(`/sync/totais/${lado}`, { params: periodo });
  return data;
};
